import React, { createContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from '../api';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    setCurrentUser(null);
    setLoading(false);
  }, []);

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await api.get('/users/me');
      setCurrentUser(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user profile', error);
      handleLogout();
    }
  }, [handleLogout]);

  useEffect(() => {
    // Check if user is logged in on page load
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Check if token is expired
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp > currentTime) {
          // Token still valid, fetch user info
          fetchUserProfile();
        } else {
          // Token expired
          handleLogout();
        }
      } catch (error) {
        console.error('Invalid token', error);
        handleLogout();
      }
    } else {
      setLoading(false);
    }
  }, [fetchUserProfile, handleLogout]);

  const handleLogin = async (email, password) => {
    try {
      const response = await api.post('/token', {
        username: email,
        password: password
      });
      
      localStorage.setItem('token', response.data.access_token);
      await fetchUserProfile();
      return { success: true };
    } catch (error) {
      console.error('Login error', error);
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.status === 401) {
          errorMessage = 'Invalid email or password. Please try again.';
        } else if (error.response.data && error.response.data.detail) {
          // Get the error message from the FastAPI backend if available
          errorMessage = error.response.data.detail;
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = 'No response from server. Please check your internet connection.';
      }
      
      return { success: false, message: errorMessage };
    }
  };

  const handleRegister = async (userData) => {
    try {
      await api.post('/users/', userData);
      const loginSuccess = await handleLogin(userData.email, userData.password);
      return loginSuccess;
    } catch (error) {
      console.error('Registration error', error);
      return false;
    }
  };

  const value = {
    currentUser,
    loading,
    login: handleLogin,
    logout: handleLogout,
    register: handleRegister,
    refreshUser: fetchUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}